/**
 * WHICH tabs the rail has, and which one is live (fix D / R3 + §T1, extended by
 * fix G / G2, 2026-10-06).
 *
 * NAMED `tabSpec` and not `railTabs`: macOS resolves `./railTabs` to the
 * `RailTabs/` unit folder beside it (see `__tests__/fileCase.test.ts` — the same
 * class of bug that once made the event dock import itself).
 *
 * Pure, and shared by the tab strip and the tab body, so the strip can never
 * highlight a tab the body does not draw. The tournament panels appear only in
 * tournament mode; 'leaderboard' only in an arena (a Swiss has no running
 * leaderboard — its own live table IS the standings); 'details' is new in G2
 * and appears in EVERY tournament, game-on-board or idle.
 *
 * Fix G folded the tournament lobby into the rail: with no board of mine up
 * (the idle "waiting for a pairing" view), Moves and Info have nothing to show,
 * so `gameOnBoard` drops them and the tournament panels become the WHOLE tab
 * strip instead of an addition to it.
 */
import type {LiveGameState} from '@/Store/liveGameStore';

import type {V2RailTab} from '../../theme/useV2Ui';
import {selectRailMode} from '../Rail/model';

export type RailTabInput = {
  tournament: boolean;
  arena: boolean;
  /** a game of mine, or a watch, owns the board; false on the idle tournament view */
  gameOnBoard: boolean;
  /**
   * Fix S / S3: the three signals that decide the `standings` tab's LABEL
   * ('Players' before the Swiss has started, 'Standings' after). Optional
   * with "nothing has happened yet" defaults so a caller that only cares
   * about the tab SET (the pre-existing tests, `tabsFor({tournament, arena,
   * gameOnBoard})`) does not have to grow a fourth argument.
   */
  ended?: boolean;
  pairingGenerated?: boolean;
  currentRound?: number | null;
  /**
   * Fix V2: on a (re)connect or a fresh store, `tournament_status_response` —
   * re-sent by S5 — is the ONLY tournament message a client gets, and it can
   * report a round under way (or finished) without `pairingGenerated` or
   * `currentRound` ever having been set by a `pairing`/`tournament_game_end`
   * broadcast this session. `started` below also treats a non-null
   * `roundState` as "something has happened", so a reload into a running or
   * finished round does not strand the strip on "Players" while the status
   * card (which reads `roundState` directly) already shows the real state.
   * `ended` is already true for `'tournament_finished'` as of this fix too.
   */
  roundState?: string | null;
};

export const selectRailTabInput = (s: LiveGameState): RailTabInput => ({
  tournament: selectRailMode(s) === 'tournament',
  arena: s.tournament.mode === 'arena',
  gameOnBoard:
    s.game.phase !== 'none' || s.watch.active || !!s.watch.replayGameId,
  ended: s.tournament.ended,
  pairingGenerated: s.tournament.pairingGenerated,
  currentRound: s.tournament.currentRound,
  roundState: s.tournament.roundState,
});

export type RailTabSpec = {key: V2RailTab; label: string};

/**
 * Swiss, game on board: Moves · Info · Details · Standings · Pairings.
 * Swiss, idle:                     Details · Standings · Pairings.
 * Arena, game on board: Moves · Info · Details · Arena · Games · Leaderboard.
 * Arena, idle:                     Details · Arena · Games · Leaderboard.
 *
 * 'standings' is RELABELLED "Arena" in arena mode (its panel becomes the old
 * `Arena/ArenaLobby`) and 'pairings' is relabelled "Games" (`Arena/ArenaGames`)
 * — the KEYS stay the same across both families so `railTab` never has to
 * change when a tournament's family is discovered after the tab was picked.
 */
export const tabsFor = ({
  tournament,
  arena,
  gameOnBoard,
  ended = false,
  pairingGenerated = false,
  currentRound = null,
  roundState = null,
}: RailTabInput): RailTabSpec[] => {
  if (!tournament) {
    return [
      {key: 'moves', label: 'Moves'},
      {key: 'info', label: 'Info'},
    ];
  }
  // S3: the Swiss `standings` tab is labelled 'Players' until the first
  // round has actually started — before that, StandingsPanel's query is
  // the joined ROSTER (§1.4 of the contract map: round 1 before any round
  // has been played returns the joined players, not a standings table), so
  // the tab should say what it is. An arena is untouched — it is always
  // 'Arena', the live lobby, which is correct at every stage.
  //
  // Fix V2: `roundState !== null` also counts — a (re)connect's
  // `tournament_status_response` can be the FIRST message this session ever
  // sees, so `pairingGenerated`/`currentRound` may still be at their "nothing
  // has happened" defaults even though the status card already shows a round
  // under way or finished.
  const started =
    ended ||
    pairingGenerated ||
    (currentRound ?? 0) >= 1 ||
    roundState !== null;
  const standingsLabel = arena ? 'Arena' : started ? 'Standings' : 'Players';

  const tabs: RailTabSpec[] = [];
  if (gameOnBoard) {
    tabs.push({key: 'moves', label: 'Moves'});
    tabs.push({key: 'info', label: 'Info'});
  }
  tabs.push({key: 'details', label: 'Details'});
  tabs.push({key: 'standings', label: standingsLabel});
  tabs.push({key: 'pairings', label: arena ? 'Games' : 'Pairings'});
  if (arena) {
    tabs.push({key: 'leaderboard', label: 'Leaderboard'});
  }
  return tabs;
};

/**
 * The tab actually on screen. A stored tab that is no longer offered (the
 * tournament was left, an arena became a Swiss, or — G2 — the idle tournament
 * view does not offer Moves/Info at all) falls back to the FIRST offered tab
 * instead of leaving the rail blank. That is deliberately the list's own
 * first entry rather than a hard-coded 'moves': for a casual game that is the
 * same thing (Moves is always first there), but for the idle tournament view
 * (no Moves/Info at all) a hard-coded 'moves' would itself not be offered and
 * `TabBody`'s switch would fall through to the live body with no game to show.
 */
export const activeTab = (tabs: RailTabSpec[], railTab: V2RailTab): V2RailTab =>
  tabs.some(tab => tab.key === railTab) ? railTab : tabs[0]?.key ?? 'moves';

/**
 * The tournament family's own "home" tab: 'leaderboard' for an arena (old
 * L3259/L6589 on the app side), 'standings' for a Swiss. Shared by the
 * "Tournament" action tile, the result card's "Go to tournament" and the idle
 * Stage's "See standings" (G5) — a single source of truth, after P1-3's lesson
 * about two copies of the same "which tab does this family land on" rule.
 */
export const resultTabFor = (arena: boolean): V2RailTab =>
  arena ? 'leaderboard' : 'standings';
